import React, { useState, useEffect } from "react";

import { Trans, useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";

import MainHeader from "../../components/Headers/MainHeader";
import BreadCrumb from "../../components/Navbars/BreadCrumb";
import VerticalRadioButtons from "../../components/RadioButtons/VerticalRadioButtons";
import ReviewProductSchema from "../../schemas/reviewProduct.schema";
import StepsNavButtons from "../../components/Buttons/StepsNavButtons";
import { ErrorAlert, SuccessAlert } from "../../utils/Alerts";
import ValidationError from "../../components/Shared/ValidationError";
import { validate } from "../../utils/ValidationHelpers";
import ProductVersionAPI from "../../API/productVersion.api";

import LabStoreProduct from "./LabStoreProduct";
import { isEmpty } from "lodash";
import SimpleNotification from "../../components/Notifications/SimpleNotification";

const ReviewProduct = () => {
    const location = useLocation();
    const { t } = useTranslation();
    const history = useHistory();

    const productVersionId = location.state?.productVersionId || "";
    const previousPage = location.state?.previousPage || {
        label: t("publishedProducts"),
        path: "/published-products",
    };

    const [isSubmitting, setIsSubmitting] = useState(false);

    //Validation States
    const [isFormValid, setIsFormValid] = useState(false);
    const [isFormSubmitted, setIsFormSubmitted] = useState(false);
    const [error, setError] = useState({});

    //Body
    const [reviewBody, setReviewBody] = useState({
        productVersionId,
        selection: "",
        comment: "",
    });

    //Product Data
    const [productData, setProductData] = useState({});
    const [parameterNames, setParameterNames] = useState([]);

    const breadCrumbs = [
        {
            path: "/dashboard",
            element: () => <i className="fas fa-home" />,
        },
        {
            hide: isEmpty(previousPage),
            path: previousPage.path,
            element: () => previousPage.label,
        },
        {
            element: () => (
                <Trans i18nKey="reviewProduct">Review Product</Trans>
            ),
        },
    ];

    const acceptProductOptions = [
        {
            name: "Accept Product",
            label: t("acceptProduct"),
            value: "Accept Product",
        },
        {
            name: "Reject Product",
            label: t("rejectProduct"),
            value: "Reject Product",
        },
    ];

    const getProductData = async () => {
        const result = await ProductVersionAPI.getReviewSubmissionData(
            productVersionId
        );

        if (!isEmpty(result)) {
            const materialNames = result.bundleProducts
                ? result.bundleProducts.map((product) => product.materialName)
                : [result.materialNames];

            setProductData({ ...result, materialNames });

            const parameters = (result.parameters || []).map(
                (parameter) => parameter.parameterName
            );
            setParameterNames(parameters);
        }
    };

    useEffect(() => {
        if (!productVersionId) {
            history.push("/page-not-found");
        } else {
            getProductData();
        }
    }, []);

    useEffect(() => {
        validate(
            reviewBody,
            ReviewProductSchema.ReviewProductForm,
            setIsFormValid,
            setError,
            {
                allowUnknown: false,
                context: {
                    isAccepted: reviewBody.selection.booleanValue,
                },
            }
        );
    }, [reviewBody]);

    const submitReview = async () => {
        setIsFormSubmitted(true);
        if (!isFormValid) return;
        const reformattedBody = {
            ...reviewBody,
            accepted: reviewBody.selection.booleanValue,
        };
        setIsSubmitting(true);

        const result = await ProductVersionAPI.reviewSubmission(
            reformattedBody
        );
        if (result.modified) {
            SuccessAlert(t("reviewProductSuccessMessage"));
            history.goBack();
        } else {
            ErrorAlert(t("reviewProductErrorMessage"));
        }
        setIsSubmitting(false);
    };

    const handleInputChange = (e) => {
        setReviewBody((prevBody) => {
            return { ...prevBody, [e.target.name]: e.target.value };
        });
    };

    const handleBooleanRadioChange = (e, trueMatch) => {
        setReviewBody((prevBody) => {
            return {
                ...prevBody,
                [e.target.name]: {
                    value: e.target.value,
                    booleanValue: e.target.value === trueMatch,
                },
            };
        });
    };

    const renderFields = () => {
        return (
            fields &&
            fields.map((field, index) => {
                if (!field.hide) {
                    const inputGroup = field.element();
                    return (
                        <div className="tags" key={index}>
                            <h4 className="uppercase text-xs required">
                                {inputGroup.label}
                            </h4>

                            {inputGroup.input}
                        </div>
                    );
                }
            })
        );
    };

    const fields = [
        {
            element: () => {
                return {
                    label: t("acceptProduct"),
                    input: (
                        <div
                            className={`input-group ${
                                error.selection && isFormSubmitted
                                    ? "invalid-warning-input"
                                    : ""
                            }`}
                        >
                            <VerticalRadioButtons
                                name="selection"
                                options={acceptProductOptions}
                                onChangeHandler={(e) => {
                                    handleBooleanRadioChange(
                                        e,
                                        "Accept Product"
                                    );
                                }}
                                checkedReference={reviewBody.selection.value}
                            />
                            {isFormSubmitted && (
                                <ValidationError
                                    errorMessage={error.selection}
                                />
                            )}
                        </div>
                    ),
                };
            },
        },
        {
            hide:
                reviewBody.selection.booleanValue ||
                reviewBody.selection === "",
            element: () => {
                return {
                    label: t("comment"),
                    input: (
                        <div
                            className={`input-group ${
                                error.comment && isFormSubmitted
                                    ? "invalid-warning-input"
                                    : ""
                            }`}
                        >
                            <textarea
                                name="comment"
                                onChange={handleInputChange}
                                value={reviewBody.comment}
                                className="input-element textarea-element"
                                placeholder={t("inputPlaceholder", {
                                    entity: "rejection comments",
                                })}
                            ></textarea>
                            {isFormSubmitted && (
                                <ValidationError errorMessage={error.comment} />
                            )}
                        </div>
                    ),
                };
            },
        },
    ];

    return (
        <>
            <BreadCrumb crumbs={breadCrumbs} />
            <MainHeader
                header={`${t("reviewProduct")} ${productData.productVersion}`}
            />
            <div className="card-table shadow flex flex-col mb-3">
                {renderFields()}
                <StepsNavButtons
                    next={{
                        action: submitReview,
                        disabled:
                            (isFormSubmitted && !isFormValid) || isSubmitting,
                        isSubmitting,
                        label: t("reviewProduct"),
                    }}
                />
            </div>

            {productData?.forceUpgrade && (
                <SimpleNotification
                    type="warnTriangle"
                    message={t("forceUpgradeReview")}
                />
            )}
            <LabStoreProduct
                product={productData}
                isPreview={true}
                isReview={true}
                previewParameterName={parameterNames}
            />
        </>
    );
};

export default ReviewProduct;
